import { KNOWN_PLACEHOLDER_TOKENS } from '../constants/mail-template-placeholders.js'

// Prüft Platzhalter in Mailvorlagen, bevor sie gespeichert werden. Dieselbe
// Erkennung färbt die Platzhalter im Editor (tiptap/extensions/placeholder-tokens).
//
// inviteTalent rendert die Vorlage mit generateHTML und ersetzt Platzhalter danach
// per replaceAll im HTML. Ein Platzhalter, dessen Zeichen unterschiedlich
// formatiert sind, steht dort als {{first<strong>name</strong>}} und wird nie
// ersetzt. Deshalb zählt ein Token nur als intakt, wenn alle seine Zeichen
// dieselben Marks tragen. Imports mit .js-Endung, damit scripts/ die Datei unter
// Node laden kann.

// Ersatzzeichen für Inline-Knoten ohne Text (hardBreak). Hat wie der Knoten im
// Dokument die Länge 1, so passen Offsets im Text und im Dokument zusammen.
export const LEAF_TEXT = '￼'

const PLACEHOLDER_PATTERN = /\{\{[^{}\n￼]*\}\}/g
const INCOMPLETE_PATTERN = /\{\{[^\s{}￼]*|[^\s{}￼]*\}\}/g
const INLINE_TYPES = ['text', 'hardBreak']

export const PLACEHOLDER_STATUS = {
  VALID: 'valid',
  UNKNOWN: 'unknown',
  NOT_ALLOWED: 'not_allowed',
  BROKEN: 'broken'
}

/**
 * Findet {{…}} in den Textstücken eines Absatzes.
 *
 * @param {{ text: string, marksKey: string, leaf?: boolean }[]} segments
 * @returns {{ token: string, start: number, end: number, intact: boolean }[]}
 */
export const scanPlaceholderSegments = (segments) => {
  let offset = 0
  const ranges = segments.map((segment) => {
    const range = {
      ...segment,
      start: offset,
      end: offset + segment.text.length
    }
    offset = range.end
    return range
  })
  const text = segments.map(({ text: part }) => part).join('')

  return [...text.matchAll(PLACEHOLDER_PATTERN)].map((match) => {
    const start = match.index
    const end = start + match[0].length
    const covered = ranges.filter(
      (range) => range.start < end && range.end > start
    )
    const intact = covered.every(
      ({ leaf, marksKey }) => !leaf && marksKey === covered[0].marksKey
    )

    return { token: match[0], start, end, intact }
  })
}

/**
 * Reste eines Platzhalters, z. B. "{{firstname" nach einem Zeilenumbruch im Token.
 *
 * @param {string} text Text eines Absatzes
 * @param {{ start: number, end: number }[]} matches vollständige Platzhalter
 * @returns {{ snippet: string }[]}
 */
export const findIncompletePlaceholders = (text, matches) => {
  let masked = text
  matches.forEach(({ start, end }) => {
    masked =
      masked.slice(0, start) + ' '.repeat(end - start) + masked.slice(end)
  })

  return [...masked.matchAll(INCOMPLETE_PATTERN)].map(([snippet]) => ({
    snippet
  }))
}

/**
 * @param {{ token: string, intact: boolean }} match
 * @param {{ known: string[], allowed: string[] }} options
 * @returns {string} ein Wert aus PLACEHOLDER_STATUS
 */
export const classifyPlaceholder = ({ token, intact }, { known, allowed }) => {
  if (!known.includes(token)) return PLACEHOLDER_STATUS.UNKNOWN
  if (!allowed.includes(token)) return PLACEHOLDER_STATUS.NOT_ALLOWED
  if (!intact) return PLACEHOLDER_STATUS.BROKEN
  return PLACEHOLDER_STATUS.VALID
}

const toMarksKey = (marks = []) =>
  marks
    .map(({ type, attrs }) => `${type}${attrs ? JSON.stringify(attrs) : ''}`)
    .sort()
    .join('|')

const toSegment = (node) =>
  node.type === 'text'
    ? { text: node.text || '', marksKey: toMarksKey(node.marks) }
    : { text: LEAF_TEXT, marksKey: '', leaf: true }

/**
 * Absätze und Überschriften eines TipTap-Dokuments als Textstücke, auch in
 * Listen und Zitaten.
 *
 * @param {object} doc TipTap-JSON
 * @returns {{ text: string, marksKey: string, leaf?: boolean }[][]}
 */
export const textblocksFromJSON = (doc) => {
  const blocks = []

  const walk = (node) => {
    if (!node?.content?.length) return
    if (node.content.every(({ type }) => INLINE_TYPES.includes(type))) {
      blocks.push(node.content.map(toSegment))
      return
    }
    node.content.forEach(walk)
  }

  walk(doc)
  return blocks
}

export const isMailBodyEmpty = (doc) =>
  textblocksFromJSON(doc).every((segments) =>
    segments.every(({ text, leaf }) => leaf || !text.trim())
  )

/**
 * Alle Probleme einer Vorlage. Eine Vorlage mit Problemen darf nicht
 * gespeichert werden, sonst landet ein rohes {{…}} in einer Kundenmail.
 *
 * @param {object} params
 * @param {object} params.doc TipTap-JSON des Mailtexts
 * @param {string} [params.subject]
 * @param {string[]} params.allowed im Mailtext erlaubte Platzhalter
 * @param {string[]} params.subjectAllowed im Betreff erlaubte Platzhalter
 * @param {string[]} [params.known] alle Platzhalter, die inviteTalent kennt
 * @returns {{ code: string, location: 'body'|'subject', token?: string, snippet?: string, count: number }[]}
 */
export const collectMailTemplateIssues = ({
  doc,
  subject,
  allowed,
  subjectAllowed,
  known = KNOWN_PLACEHOLDER_TOKENS
}) => {
  const issues = new Map()
  const add = (issue) => {
    const id = [issue.location, issue.code, issue.token || issue.snippet].join(
      ':'
    )
    const existing = issues.get(id)
    if (existing) existing.count += 1
    else issues.set(id, { ...issue, count: 1 })
  }

  const check = (segments, location, allowedHere) => {
    const matches = scanPlaceholderSegments(segments)

    matches.forEach((match) => {
      const status = classifyPlaceholder(match, { known, allowed: allowedHere })
      if (status !== PLACEHOLDER_STATUS.VALID) {
        add({ code: status, token: match.token, location })
      }
    })

    const text = segments.map(({ text: part }) => part).join('')
    findIncompletePlaceholders(text, matches).forEach(({ snippet }) =>
      add({ code: 'incomplete', snippet, location })
    )
  }

  if (isMailBodyEmpty(doc)) add({ code: 'empty_body', location: 'body' })

  textblocksFromJSON(doc).forEach((segments) =>
    check(segments, 'body', allowed)
  )

  if (subject) {
    check([{ text: subject, marksKey: '' }], 'subject', subjectAllowed)
  }

  return [...issues.values()]
}
